import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authApi, callCampaignsApi, campaignsApi } from "@/api";
import type { SegmentFilter, TurnNode, ProspectSegmentFilter, AudienceType } from "@/types";
import { CustomFlowEditor } from "@/components/call-campaign-editor/CustomFlowEditor";
import { DraftActionPanel } from "@/components/call-campaign-editor/DraftActionPanel";
import { TestCallPanel } from "@/components/call-campaign-editor/TestCallPanel";
import { TtsSettingsPanel } from "@/components/call-campaign-editor/TtsSettingsPanel";
import {
  TERMINAL_ACTIONS, TRANSFER_WAIT,
  DEFAULT_TURNS_TEMPLATE,
} from "@/types";
import { ArrowLeft } from "lucide-react";

const traderTypeOptions = [
  "業販・取付", "業販", "一次代理店", "代理店", "ディーラー",
  "取付", "二次代理店", "販社", "0次代理店", "三次代理店",
];
const sourceOptions = [
  { value: "TRADER", label: "取引先" },
  { value: "TRADER_CONTACT", label: "担当者" },
  { value: "MEMBER", label: "会員" },
];
const rfmRankOptions = ["A", "B", "C", "D"];
const prospectFreshnessOptions = [
  { value: "FRESH", label: "新しい" },
  { value: "AGING", label: "経時" },
  { value: "STALE", label: "古い" },
];
const prospectVerificationOptions = [
  { value: "UNVERIFIED", label: "未確認" },
  { value: "VERIFIED", label: "確認済み" },
  { value: "UNREACHABLE", label: "不通" },
  { value: "REFUSED", label: "拒否" },
  { value: "INVALID", label: "無効" },
];
const prospectIndustryFitOptions = [
  { value: "HIGH", label: "高適合" },
  { value: "MEDIUM", label: "中適合" },
  { value: "LOW", label: "低適合" },
  { value: "UNKNOWN", label: "不明" },
];
function parseCsvParam(value: string | null): string[] {
  return value ? value.split(",").map((item) => item.trim()).filter(Boolean) : [];
}

function toDateTimeLocalValue(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return localDate.toISOString().slice(0, 16);
}

function CheckboxGroup({
  label, options, selected, onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (vals: string[]) => void;
}) {
  const toggle = (val: string) => {
    onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val]);
  };

  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className={`px-2 py-1 rounded text-xs border transition-colors ${
              selected.includes(opt.value)
                ? "bg-blue-500 text-white border-blue-500"
                : "bg-white text-slate-600 border-slate-300 hover:border-blue-300"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// --- Validation ---

function validateTurns(nodes: TurnNode[]): string[] {
  const errors: string[] = [];
  if (nodes.length === 0) {
    errors.push("ノードが1つもありません");
    return errors;
  }

  const entries = nodes.filter((n) => n.isEntry);
  if (entries.length !== 1) {
    errors.push(`開始ノードは1つだけ必要です（現在: ${entries.length}つ）`);
  }

  const ids = new Set<string>();
  for (const n of nodes) {
    if (!n.id) {
      errors.push("IDが空のノードがあります");
    } else if (!/^[a-z0-9_]+$/.test(n.id)) {
      errors.push(`ノード "${n.id}": IDは半角英数小文字と_のみ`);
    }
    if (ids.has(n.id)) {
      errors.push(`ノードID "${n.id}" が重複しています`);
    }
    ids.add(n.id);
    if (!n.speech) {
      errors.push(`ノード "${n.id}": 発話が空です`);
    }
  }

  const validTargets = new Set([
    ...ids,
    ...TERMINAL_ACTIONS,
    TRANSFER_WAIT,
  ]);
  for (const n of nodes) {
    for (const [intent, t] of Object.entries(n.transitions)) {
      if (t && !validTargets.has(t.target)) {
        errors.push(`ノード "${n.id}" のインテント "${intent}": 遷移先 "${t.target}" が無効です`);
      }
    }
  }

  // Reachability: all non-entry nodes should be reachable from some transition
  const reachable = new Set<string>();
  for (const n of nodes) {
    for (const t of Object.values(n.transitions)) {
      if (t && ids.has(t.target)) reachable.add(t.target);
    }
  }
  for (const n of nodes) {
    if (!n.isEntry && !reachable.has(n.id)) {
      errors.push(`ノード "${n.id}" (${n.name}) はどこからも到達できません`);
    }
  }

  return errors;
}

// --- Main Editor ---

export default function CallCampaignEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const isEdit = !!id;
  const { data: appConfig } = useQuery({
    queryKey: ["app-config"],
    queryFn: () => authApi.appConfig(),
  });

  const [name, setName] = useState("");
  const [scriptName, _setScriptName] = useState("");
  const [greeting, setGreeting] = useState(
    "お世話になります。{company}です。自動車のヘッドライトまわりのご担当者様はいらっしゃいますか？"
  );
  const [productName, setProductName] = useState("HID車向けLEDバルブ");
  const [productPitch, setProductPitch] = useState("純正HID車のロービーム車検対策");
  const [callbackMessage, setCallbackMessage] = useState("ありがとうございます。担当から短くご案内いたします。ご都合が悪ければその場で調整いたします。");
  const [farewellMessage, _setFarewellMessage] = useState("お時間をいただきありがとうございました。失礼いたします。");
  const [companyName, setCompanyName] = useState("");
  const [ttsProvider, setTtsProvider] = useState<"openai" | "google">("openai");
  const [ttsModel, setTtsModel] = useState("tts-1");
  const [ttsVoice, setTtsVoice] = useState("nova");
  const [ttsInstructions, setTtsInstructions] = useState("");

  // Script mode
  const [scriptMode, setScriptMode] = useState<"simple" | "custom">("simple");
  const [turnNodes, setTurnNodes] = useState<TurnNode[]>([]);
  const [turnsErrors, setTurnsErrors] = useState<string[]>([]);

  // AI flow generation
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPreview, setAiPreview] = useState<TurnNode[] | null>(null);
  const [scheduledAt, setScheduledAt] = useState("");
  const [showSchedule, setShowSchedule] = useState(false);

  useEffect(() => {
    if (!isEdit && !companyName && appConfig?.brandShortName) {
      setCompanyName(appConfig.brandShortName);
    }
  }, [appConfig?.brandShortName, companyName, isEdit]);

  // Segment
  const [audienceType, setAudienceType] = useState<AudienceType>(
    searchParams.get("audienceType") === "PROSPECT" ? "PROSPECT" : "CONTACT"
  );
  const [traderType, setTraderType] = useState<string[]>([]);
  const [source, setSource] = useState<string[]>([]);
  const [rfmRank, setRfmRank] = useState<string[]>([]);
  const [leadScoreMin, setLeadScoreMin] = useState("");
  const [prospectListSource, setProspectListSource] = useState(searchParams.get("listSource") || "");
  const [prospectListVersion, setProspectListVersion] = useState(searchParams.get("listVersion") || "");
  const [prospectFreshness, setProspectFreshness] = useState<string[]>(parseCsvParam(searchParams.get("freshnessStatus")));
  const [prospectVerification, setProspectVerification] = useState<string[]>(parseCsvParam(searchParams.get("verificationStatus")));
  const [prospectIndustryFit, setProspectIndustryFit] = useState<string[]>(parseCsvParam(searchParams.get("industryFit")));
  const [pinnedContactIds, setPinnedContactIds] = useState<string[]>([]);
  const [pinnedProspectIds, setPinnedProspectIds] = useState<string[]>([]);
  const [prospectPrefecture, setProspectPrefecture] = useState(searchParams.get("prefecture") || "");
  const [prospectHasLastOrder, setProspectHasLastOrder] = useState<"" | "true" | "false">(
    searchParams.get("hasLastOrder") === "true" ? "true" : searchParams.get("hasLastOrder") === "false" ? "false" : ""
  );
  const [prospectUntouchedOnly, setProspectUntouchedOnly] = useState(searchParams.get("outgoingCallCountMax") === "0");
  const [prospectHasPhone, setProspectHasPhone] = useState(searchParams.get("hasPhone") !== "false");
  const [prospectCompanySearch, setProspectCompanySearch] = useState(searchParams.get("companySearch") || "");
  const [prospectSourceUpdatedBefore, setProspectSourceUpdatedBefore] = useState("");

  // Connected campaign
  const [emailCampaignId, setEmailCampaignId] = useState("");
  const [triggerEvent, setTriggerEvent] = useState("EMAIL_OPEN");
  const [triggerDays, setTriggerDays] = useState("3");

  // Test call
  const [testPhone, setTestPhone] = useState("");
  const [testName, setTestName] = useState("");
  const [testCompany, setTestCompany] = useState("");
  const [testStatus, setTestStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const [error, setError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Load existing campaign
  const { data: existing } = useQuery({
    queryKey: ["call-campaign", id],
    queryFn: () => callCampaignsApi.get(id!),
    enabled: isEdit,
  });

  // Load email campaigns for connection
  const { data: emailCampaigns } = useQuery({
    queryKey: ["campaigns-sent"],
    queryFn: () => campaignsApi.list({ status: "SENT" }),
  });

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setScheduledAt(toDateTimeLocalValue(existing.scheduledAt));
      setAudienceType(existing.audienceType || "CONTACT");
      if ((existing.audienceType || "CONTACT") === "PROSPECT") {
        const seg = (existing.segmentQuery as ProspectSegmentFilter) || {};
        setProspectListSource(seg.listSource?.join(",") || "");
        setProspectListVersion(seg.listVersion?.join(",") || "");
        setProspectFreshness(seg.freshnessStatus || []);
        setProspectVerification(seg.verificationStatus || []);
        setProspectIndustryFit(seg.industryFit || []);
        setPinnedProspectIds(seg.prospectIds || []);
        setProspectPrefecture(seg.prefecture?.join(",") || "");
        setProspectHasLastOrder(
          seg.hasLastOrder === true ? "true" : seg.hasLastOrder === false ? "false" : ""
        );
        setProspectUntouchedOnly(seg.outgoingCallCountMax === 0);
        setProspectHasPhone(seg.hasPhone !== false);
        setProspectCompanySearch(seg.companySearch || "");
        setProspectSourceUpdatedBefore(
          seg.sourceUpdatedBefore !== undefined ? String(seg.sourceUpdatedBefore) : ""
        );
      } else {
        const seg = (existing.segmentQuery as SegmentFilter) || {};
        setTraderType(seg.traderType || []);
        setSource(seg.source || []);
        setRfmRank(seg.rfmRank || []);
        setPinnedContactIds(seg.contactIds || []);
        if (seg.leadScoreMin !== undefined) setLeadScoreMin(String(seg.leadScoreMin));
      }
      if (existing.emailCampaignId) setEmailCampaignId(existing.emailCampaignId);
      if (existing.triggerRule) {
        const rule = existing.triggerRule as Record<string, unknown>;
        setTriggerEvent((rule.event as string) || "EMAIL_OPEN");
        setTriggerDays(String(rule.noClickWithinDays || 3));
      }
      // Populate script fields from existing campaign
      if ((existing as any).callScript) {
        const script = (existing as any).callScript;
        if (script.greeting) setGreeting(script.greeting);
        if (script.productName) setProductName(script.productName);
        if (script.productPitch) setProductPitch(script.productPitch);
        if (script.callbackMessage) setCallbackMessage(script.callbackMessage);
        if (script.farewellMessage) _setFarewellMessage(script.farewellMessage);
        if (script.name) _setScriptName(script.name);
        if (script.companyName) setCompanyName(script.companyName);
        if (script.ttsProvider) setTtsProvider(script.ttsProvider);
        if (script.ttsModel) setTtsModel(script.ttsModel);
        if (script.ttsVoice) setTtsVoice(script.ttsVoice);
        if (script.ttsInstructions) setTtsInstructions(script.ttsInstructions);
        // Load turns (custom flow)
        if (script.turns && Array.isArray(script.turns) && script.turns.length > 0) {
          setScriptMode("custom");
          setTurnNodes(script.turns as TurnNode[]);
        }
      }
    }
  }, [existing]);

  // Segment preview
  const buildSegmentParams = useCallback(() => {
    const params: Record<string, string> = {};
    params.audienceType = audienceType;
    if (audienceType === "PROSPECT") {
      if (pinnedProspectIds.length) params.prospectIds = pinnedProspectIds.join(",");
      if (prospectListSource.trim()) params.listSource = prospectListSource.trim();
      if (prospectListVersion.trim()) params.listVersion = prospectListVersion.trim();
      if (prospectFreshness.length) params.freshnessStatus = prospectFreshness.join(",");
      if (prospectVerification.length) params.verificationStatus = prospectVerification.join(",");
      if (prospectIndustryFit.length) params.industryFit = prospectIndustryFit.join(",");
      if (prospectPrefecture.trim()) params.prefecture = prospectPrefecture.trim();
      if (prospectHasLastOrder) params.hasLastOrder = prospectHasLastOrder;
      if (prospectUntouchedOnly) params.outgoingCallCountMax = "0";
      if (prospectHasPhone) params.hasPhone = "true";
      if (prospectCompanySearch.trim()) params.companySearch = prospectCompanySearch.trim();
      if (prospectSourceUpdatedBefore) params.sourceUpdatedBefore = prospectSourceUpdatedBefore;
      return params;
    }
    if (pinnedContactIds.length) params.contactIds = pinnedContactIds.join(",");
    if (traderType.length) params.traderType = traderType.join(",");
    if (source.length) params.source = source.join(",");
    if (rfmRank.length) params.rfmRank = rfmRank.join(",");
    if (leadScoreMin) params.leadScoreMin = leadScoreMin;
    return params;
  }, [
    audienceType,
    leadScoreMin,
    prospectCompanySearch,
    prospectFreshness,
    prospectHasLastOrder,
    prospectHasPhone,
    prospectIndustryFit,
    prospectListSource,
    prospectListVersion,
    pinnedContactIds,
    pinnedProspectIds,
    prospectPrefecture,
    prospectSourceUpdatedBefore,
    prospectUntouchedOnly,
    prospectVerification,
    rfmRank,
    source,
    traderType,
  ]);

  const { data: preview } = useQuery({
    queryKey: ["call-segment-preview", buildSegmentParams()],
    queryFn: () => callCampaignsApi.segmentPreview(buildSegmentParams()),
    enabled: Object.keys(buildSegmentParams()).length > 0,
  });

  const buildSegmentQuery = (): SegmentFilter | ProspectSegmentFilter => {
    if (audienceType === "PROSPECT") {
      const seg: ProspectSegmentFilter = {};
      if (pinnedProspectIds.length) seg.prospectIds = pinnedProspectIds;
      if (prospectListSource.trim()) seg.listSource = parseCsvParam(prospectListSource);
      if (prospectListVersion.trim()) seg.listVersion = parseCsvParam(prospectListVersion);
      if (prospectFreshness.length) seg.freshnessStatus = prospectFreshness as ProspectSegmentFilter["freshnessStatus"];
      if (prospectVerification.length) seg.verificationStatus = prospectVerification as ProspectSegmentFilter["verificationStatus"];
      if (prospectIndustryFit.length) seg.industryFit = prospectIndustryFit as ProspectSegmentFilter["industryFit"];
      if (prospectPrefecture.trim()) seg.prefecture = parseCsvParam(prospectPrefecture);
      if (prospectHasLastOrder) seg.hasLastOrder = prospectHasLastOrder === "true";
      if (prospectUntouchedOnly) seg.outgoingCallCountMax = 0;
      if (prospectHasPhone) seg.hasPhone = true;
      if (prospectCompanySearch.trim()) seg.companySearch = prospectCompanySearch.trim();
      if (prospectSourceUpdatedBefore) seg.sourceUpdatedBefore = parseInt(prospectSourceUpdatedBefore, 10);
      return seg;
    }
    const seg: SegmentFilter = {};
    if (pinnedContactIds.length) seg.contactIds = pinnedContactIds;
    if (traderType.length) seg.traderType = traderType;
    if (source.length) seg.source = source;
    if (rfmRank.length) seg.rfmRank = rfmRank;
    if (leadScoreMin) seg.leadScoreMin = parseInt(leadScoreMin, 10);
    return seg;
  };

  // Validate turns on change
  useEffect(() => {
    if (scriptMode === "custom") {
      setTurnsErrors(validateTurns(turnNodes));
    } else {
      setTurnsErrors([]);
    }
  }, [turnNodes, scriptMode]);

  // Node operations
  const updateNode = (index: number, updated: TurnNode) => {
    setTurnNodes((prev) => prev.map((n, i) => (i === index ? updated : n)));
  };

  const deleteNode = (index: number) => {
    setTurnNodes((prev) => prev.filter((_, i) => i !== index));
  };

  const addNode = () => {
    if (turnNodes.length >= 10) return;
    const num = turnNodes.length + 1;
    setTurnNodes((prev) => [
      ...prev,
      {
        id: `node_${num}`,
        name: `ノード${num}`,
        speech: "",
        transitions: {},
      },
    ]);
  };

  const setEntryNode = (index: number) => {
    setTurnNodes((prev) =>
      prev.map((n, i) => ({ ...n, isEntry: i === index }))
    );
  };

  const loadTemplate = () => {
    setTurnNodes(structuredClone(DEFAULT_TURNS_TEMPLATE));
  };

  const generateWithAi = async () => {
    if (!aiPrompt.trim() || aiGenerating) return;
    setAiGenerating(true);
    setAiPreview(null);
    setError("");
    try {
      const result = await callCampaignsApi.generateFlow(aiPrompt.trim());
      if (result.nodes && Array.isArray(result.nodes) && result.nodes.length > 0) {
        setAiPreview(result.nodes);
      } else {
        setError("AIがフローを生成できませんでした");
      }
    } catch (err: any) {
      setError(err.message || "AI生成に失敗しました");
    } finally {
      setAiGenerating(false);
    }
  };

  const applyAiPreviewAll = () => {
    if (aiPreview) {
      setTurnNodes(aiPreview);
      setAiPreview(null);
    }
  };

  const applyAiNode = (node: TurnNode) => {
    setTurnNodes((prev) => {
      const idx = prev.findIndex((n) => n.id === node.id);
      if (idx >= 0) {
        // Replace existing node
        return prev.map((n, i) => (i === idx ? node : n));
      }
      // Append new node
      return [...prev, node];
    });
    // Remove from preview
    setAiPreview((prev) => {
      if (!prev) return null;
      const remaining = prev.filter((n) => n.id !== node.id);
      return remaining.length > 0 ? remaining : null;
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const seg = buildSegmentQuery();

      // Validate turns before save
      if (scriptMode === "custom") {
        const errs = validateTurns(turnNodes);
        if (errs.length > 0) {
          throw new Error(errs[0]);
        }
      }

      const script: Record<string, unknown> = {
        name: scriptName || name,
        greeting,
        productName,
        productPitch,
        callbackMessage,
        farewellMessage,
        companyName,
        ttsProvider,
        ttsModel,
        ttsVoice,
        ttsInstructions,
        turns: scriptMode === "custom" ? turnNodes : null,
      };
      const triggerRule = emailCampaignId
        ? { event: triggerEvent, noClickWithinDays: parseInt(triggerDays) || 3 }
        : undefined;

      if (isEdit) {
        return callCampaignsApi.update(id!, {
          name,
          audienceType,
          segmentQuery: seg,
          script,
          emailCampaignId: audienceType === "PROSPECT" ? null : emailCampaignId || null,
          triggerRule: audienceType === "PROSPECT" ? null : triggerRule || null,
        });
      } else {
        return callCampaignsApi.create({
          name,
          audienceType,
          segmentQuery: seg,
          script: script as any,
          emailCampaignId: audienceType === "PROSPECT" ? undefined : emailCampaignId || undefined,
          triggerRule: audienceType === "PROSPECT" ? undefined : triggerRule,
        });
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["call-campaigns"] });
      setError("");
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      if (!isEdit) {
        navigate(`/call-campaigns/${data.id}/edit`);
      }
    },
    onError: (err: any) => {
      setSaveSuccess(false);
      setError(err.message || "保存に失敗しました");
    },
  });

  const startMutation = useMutation({
    mutationFn: async () => {
      // Always save before starting to ensure latest changes are persisted
      const saved = await saveMutation.mutateAsync();
      const campaignId = saved.id || id;
      return callCampaignsApi.start(campaignId!);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["call-campaigns"] });
      navigate(`/call-campaigns/${id}`);
    },
    onError: (err: any) => setError(err.message || "開始に失敗しました"),
  });

  const scheduleMutation = useMutation({
    mutationFn: async () => {
      if (!scheduledAt) throw new Error("予約日時を指定してください");
      const dt = new Date(scheduledAt);
      if (dt <= new Date()) throw new Error("過去の日時は指定できません");
      const saved = await saveMutation.mutateAsync();
      const campaignId = saved.id || id;
      if (!campaignId) throw new Error("キャンペーンの保存に失敗しました");
      return callCampaignsApi.schedule(campaignId, dt.toISOString());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["call-campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["call-campaign", id] });
      navigate(`/call-campaigns/${id}`);
    },
    onError: (err: any) => setError(err.message || "予約に失敗しました"),
  });

  const testCallMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("キャンペーンを先に保存してください");
      setTestStatus("sending");
      return callCampaignsApi.testCall(id, {
        phone: testPhone.replace(/-/g, ""),
        name: testName || undefined,
        company: testCompany || undefined,
      });
    },
    onSuccess: () => setTestStatus("sent"),
    onError: (err: any) => {
      setTestStatus("error");
      setError(err.message || "テスト架電に失敗しました");
    },
  });

  const isDraft = !existing || existing.status === "DRAFT";

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button
        onClick={() => navigate("/call-campaigns")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"
      >
        <ArrowLeft size={16} /> 架電キャンペーン一覧
      </button>

      <h1 className="text-2xl font-bold text-slate-900 mb-6">
        {isEdit ? "架電キャンペーン編集" : "新規架電キャンペーン"}
      </h1>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
      )}

      <div className="space-y-6">
        {/* Basic info */}
        <div className="bg-white rounded-lg border p-4 space-y-4">
          <h2 className="font-semibold text-slate-900">基本設定</h2>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">キャンペーン名</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!isDraft}
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-slate-50"
              placeholder="例: 3月RIZING案内架電"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">母集団</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setAudienceType("CONTACT")}
                disabled={!isDraft}
                className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                  audienceType === "CONTACT"
                    ? "bg-slate-700 text-white border-slate-700"
                    : "bg-white text-slate-700 border-slate-300"
                } disabled:opacity-60`}
              >
                既存コンタクト
              </button>
              <button
                type="button"
                onClick={() => setAudienceType("PROSPECT")}
                disabled={!isDraft}
                className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                  audienceType === "PROSPECT"
                    ? "bg-slate-700 text-white border-slate-700"
                    : "bg-white text-slate-700 border-slate-300"
                } disabled:opacity-60`}
              >
                見込みリスト
              </button>
            </div>
          </div>
          {(pinnedContactIds.length > 0 || pinnedProspectIds.length > 0) && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              残件スナップショット固定中:
              {pinnedContactIds.length > 0 ? ` コンタクト ${pinnedContactIds.length}件` : ""}
              {pinnedProspectIds.length > 0 ? ` 見込み ${pinnedProspectIds.length}件` : ""}
            </div>
          )}
        </div>

        {/* Script settings */}
        <div className="bg-white rounded-lg border p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">トークスクリプト</h2>
            {isDraft && (
              <div className="flex rounded-lg border overflow-hidden text-xs">
                <button
                  type="button"
                  onClick={() => setScriptMode("simple")}
                  className={`px-3 py-1.5 transition-colors ${
                    scriptMode === "simple"
                      ? "bg-slate-700 text-white"
                      : "bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  シンプル
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setScriptMode("custom");
                    if (turnNodes.length === 0) loadTemplate();
                  }}
                  className={`px-3 py-1.5 transition-colors ${
                    scriptMode === "custom"
                      ? "bg-slate-700 text-white"
                      : "bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  カスタムフロー
                </button>
              </div>
            )}
          </div>

          {scriptMode === "simple" ? (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">挨拶文</label>
                <textarea
                  value={greeting}
                  onChange={(e) => setGreeting(e.target.value)}
                  disabled={!isDraft}
                  rows={2}
                  className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-slate-50"
                  placeholder="お忙しいところ恐れ入ります..."
                />
                <p className="text-xs text-slate-400 mt-1">
                  {"差し込み変数: {company}, {name}"}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">商品名</label>
                  <input
                    type="text"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    disabled={!isDraft}
                    className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-slate-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">商品ピッチ</label>
                  <input
                    type="text"
                    value={productPitch}
                    onChange={(e) => setProductPitch(e.target.value)}
                    disabled={!isDraft}
                    className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-slate-50"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">コールバック案内文</label>
                <input
                  type="text"
                  value={callbackMessage}
                  onChange={(e) => setCallbackMessage(e.target.value)}
                  disabled={!isDraft}
                  className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-slate-50"
                />
              </div>
            </>
          ) : (
            <>
              {/* Custom flow editor */}
              <p className="text-xs text-slate-500">
                ノードごとに発話と遷移ルールを設定します。未定義のインテントは同じノードに留まります。
              </p>

              {/* Variable settings */}
              <div className="p-3 bg-slate-50 rounded-lg space-y-3">
                <label className="block text-xs font-semibold text-slate-700">差し込み変数</label>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[10px] text-slate-500 mb-0.5">{"{company}"} — 自社名</label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      disabled={!isDraft}
                      className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-500 mb-0.5">{"{product_name}"} — 商品名</label>
                    <input
                      type="text"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      disabled={!isDraft}
                      className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-500 mb-0.5">{"{product_pitch}"} — 商品説明</label>
                    <input
                      type="text"
                      value={productPitch}
                      onChange={(e) => setProductPitch(e.target.value)}
                      disabled={!isDraft}
                      className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-white"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-slate-400">
                  {"{name}"} は架電先の担当者名に自動置換されます
                </p>
              </div>

              <TtsSettingsPanel
                isDraft={isDraft}
                ttsProvider={ttsProvider}
                ttsModel={ttsModel}
                ttsVoice={ttsVoice}
                ttsInstructions={ttsInstructions}
                onProviderChange={setTtsProvider}
                onModelChange={setTtsModel}
                onVoiceChange={setTtsVoice}
                onInstructionsChange={setTtsInstructions}
              />
              <CustomFlowEditor
                isDraft={isDraft}
                companyName={companyName}
                productName={productName}
                productPitch={productPitch}
                turnNodes={turnNodes}
                turnsErrors={turnsErrors}
                aiPrompt={aiPrompt}
                aiGenerating={aiGenerating}
                aiPreview={aiPreview}
                onCompanyNameChange={setCompanyName}
                onProductNameChange={setProductName}
                onProductPitchChange={setProductPitch}
                onAiPromptChange={setAiPrompt}
                onUpdateNode={updateNode}
                onDeleteNode={deleteNode}
                onSetEntryNode={setEntryNode}
                onAddNode={addNode}
                onLoadTemplate={loadTemplate}
                onGenerateWithAi={generateWithAi}
                onApplyAiPreviewAll={applyAiPreviewAll}
                onApplyAiNode={applyAiNode}
                onDiscardAiPreview={() => setAiPreview(null)}
              />
            </>
          )}
        </div>

        {/* Segment */}
        <div className="bg-white rounded-lg border p-4 space-y-4">
          <h2 className="font-semibold text-slate-900">セグメント</h2>
          {audienceType === "PROSPECT" ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">listSource</label>
                  <input
                    type="text"
                    value={prospectListSource}
                    onChange={(e) => setProspectListSource(e.target.value)}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                    placeholder="townpage_refined"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">listVersion</label>
                  <input
                    type="text"
                    value={prospectListVersion}
                    onChange={(e) => setProspectListVersion(e.target.value)}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                    placeholder="2026-03-08"
                  />
                </div>
              </div>
              <CheckboxGroup
                label="鮮度"
                options={prospectFreshnessOptions}
                selected={prospectFreshness}
                onChange={setProspectFreshness}
              />
              <CheckboxGroup
                label="確認状況"
                options={prospectVerificationOptions}
                selected={prospectVerification}
                onChange={setProspectVerification}
              />
              <CheckboxGroup
                label="業種適合"
                options={prospectIndustryFitOptions}
                selected={prospectIndustryFit}
                onChange={setProspectIndustryFit}
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">会社名検索</label>
                  <input
                    type="text"
                    value={prospectCompanySearch}
                    onChange={(e) => setProspectCompanySearch(e.target.value)}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                    placeholder="会社名"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">sourceUpdatedBefore(日)</label>
                  <input
                    type="number"
                    value={prospectSourceUpdatedBefore}
                    onChange={(e) => setProspectSourceUpdatedBefore(e.target.value)}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                    placeholder="365"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">都道府県</label>
                  <input
                    type="text"
                    value={prospectPrefecture}
                    onChange={(e) => setProspectPrefecture(e.target.value)}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                    placeholder="茨城県"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">受注履歴</label>
                  <select
                    value={prospectHasLastOrder}
                    onChange={(e) => setProspectHasLastOrder(e.target.value as "" | "true" | "false")}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                  >
                    <option value="">すべて</option>
                    <option value="false">受注履歴なし</option>
                    <option value="true">受注履歴あり</option>
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={prospectUntouchedOnly}
                  onChange={(e) => setProspectUntouchedOnly(e.target.checked)}
                />
                未接触のみ
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={prospectHasPhone}
                  onChange={(e) => setProspectHasPhone(e.target.checked)}
                />
                電話番号ありのみ
              </label>
            </>
          ) : (
            <>
              <CheckboxGroup
                label="取引先タイプ"
                options={traderTypeOptions.map((v) => ({ value: v, label: v }))}
                selected={traderType}
                onChange={setTraderType}
              />
              <CheckboxGroup
                label="ソース"
                options={sourceOptions}
                selected={source}
                onChange={setSource}
              />
              <CheckboxGroup
                label="RFMランク"
                options={rfmRankOptions.map((v) => ({ value: v, label: v }))}
                selected={rfmRank}
                onChange={setRfmRank}
              />
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  リードスコア下限
                </label>
                <input
                  type="number"
                  value={leadScoreMin}
                  onChange={(e) => setLeadScoreMin(e.target.value)}
                  className="border rounded px-2 py-1 text-sm w-24"
                  placeholder="0"
                />
              </div>
            </>
          )}
          {preview && (
            <div className="p-3 bg-blue-50 rounded-lg text-sm">
              <p>セグメント合計: <strong>{preview.total}</strong>件</p>
              <p>電話番号あり: <strong className="text-green-600">{preview.withPhone}</strong>件（架電対象）</p>
            </div>
          )}
        </div>

        {/* Connected campaign */}
        <div className="bg-white rounded-lg border p-4 space-y-4">
          <h2 className="font-semibold text-slate-900">接続キャンペーン（オプション）</h2>
          {audienceType === "PROSPECT" ? (
            <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
              見込みリスト向け架電はメールキャンペーン連動を使いません。掘り起こし条件を prospect 側で直接切って開始します。
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-500">メールキャンペーンと連動して、開封者に自動架電します</p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">メールキャンペーン</label>
                <select
                  value={emailCampaignId}
                  onChange={(e) => setEmailCampaignId(e.target.value)}
                  disabled={!isDraft}
                  className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-slate-50"
                >
                  <option value="">接続しない</option>
                  {emailCampaigns?.data.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              {emailCampaignId && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">トリガーイベント</label>
                    <select
                      value={triggerEvent}
                      onChange={(e) => setTriggerEvent(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="EMAIL_OPEN">メール開封</option>
                      <option value="EMAIL_CLICK">リンククリック</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      待機日数（クリック未なし）
                    </label>
                    <input
                      type="number"
                      value={triggerDays}
                      onChange={(e) => setTriggerDays(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      min="1"
                      max="30"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Test call */}
        {isEdit && (
          <TestCallPanel
            testPhone={testPhone}
            testName={testName}
            testCompany={testCompany}
            testStatus={testStatus}
            isPending={testCallMutation.isPending}
            onPhoneChange={setTestPhone}
            onNameChange={setTestName}
            onCompanyChange={setTestCompany}
            onSubmit={() => testCallMutation.mutate()}
          />
        )}

        {/* Actions */}
        {isDraft && (
          <DraftActionPanel
            isEdit={isEdit}
            name={name}
            previewWithPhone={preview?.withPhone}
            scheduledAt={scheduledAt}
            showSchedule={showSchedule}
            saveSuccess={saveSuccess}
            canSubmit={!(scriptMode === "custom" && turnsErrors.length > 0)}
            savePending={saveMutation.isPending}
            startPending={startMutation.isPending}
            schedulePending={scheduleMutation.isPending}
            onScheduledAtChange={setScheduledAt}
            onToggleSchedule={() => setShowSchedule((current) => !current)}
            onSchedule={() => scheduleMutation.mutate()}
            onSave={() => saveMutation.mutate()}
            onStart={() => startMutation.mutate()}
            onValidationError={setError}
          />
        )}
      </div>
    </div>
  );
}
